// apps/web/src/offline/OfflineSpinService.js

import {
  STORES,
  createLocalId,
  getOfflineDatabase,
} from './db'

const SPIN_STATUS = {
  PENDING_SYNC: 'PENDING_SYNC',
  SYNCED: 'SYNCED',
  ERROR: 'ERROR',
}

/*
 * Gera um número entre 0 e 1.
 *
 * Preferimos crypto.getRandomValues
 * quando disponível.
 */
const secureRandom = () => {
  if (
    window.crypto &&
    window.crypto.getRandomValues
  ) {
    const array =
      new Uint32Array(1)

    window.crypto.getRandomValues(
      array,
    )

    return (
      array[0] /
      4294967296
    )
  }

  return Math.random()
}

const normalizeStock = (
  value,
) => {
  if (
    value === null ||
    value === undefined
  ) {
    /*
     * null = sem controle de estoque
     */
    return null
  }

  const stock =
    Number(value)

  if (
    Number.isNaN(stock)
  ) {
    return 0
  }

  return Math.max(
    0,
    stock,
  )
}

const normalizeWeight = (
  value,
) => {
  const weight =
    Number(value)

  if (
    Number.isNaN(weight) ||
    weight <= 0
  ) {
    return 0
  }

  return weight
}

/*
 * Decide se o prêmio ainda pode
 * participar do sorteio.
 */
const isPrizeAvailable = (
  prize,
) => {
  if (!prize) {
    return false
  }

  if (
    prize.isActive === false
  ) {
    return false
  }

  const stock =
    normalizeStock(
      prize.stockAvailable,
    )

  /*
   * null significa estoque ilimitado/
   * não controlado.
   */
  if (stock === null) {
    return true
  }

  return stock > 0
}

/*
 * Sorteio ponderado.
 *
 * probabilityWeight maior =
 * maior chance de sair.
 */
const selectPrizeByWeight = (
  prizes,
) => {
  const availablePrizes =
    prizes.filter(
      isPrizeAvailable,
    )

  if (
    !availablePrizes.length
  ) {
    return null
  }

  const weightedPrizes =
    availablePrizes
      .map((prize) => ({
        prize,

        weight:
          normalizeWeight(
            prize.probabilityWeight,
          ),
      }))
      .filter(
        (item) =>
          item.weight > 0,
      )

  if (
    !weightedPrizes.length
  ) {
    return null
  }

  const totalWeight =
    weightedPrizes.reduce(
      (total, item) =>
        total +
        item.weight,
      0,
    )

  const randomValue =
    secureRandom() *
    totalWeight

  let accumulated = 0

  for (
    const item
    of weightedPrizes
  ) {
    accumulated +=
      item.weight

    if (
      randomValue <
      accumulated
    ) {
      return item.prize
    }
  }

  /*
   * Proteção contra arredondamento
   * de ponto flutuante.
   */
  return weightedPrizes[
    weightedPrizes.length - 1
  ].prize
}

/*
 * Faz uma participação offline completa
 * dentro de uma transaction IndexedDB.
 *
 * Isso evita dois giros locais lerem
 * o mesmo estoque ao mesmo tempo.
 */
const createOfflineSpin = async ({
  game,
  leadId = null,
  participant = null,
}) => {
  if (!game?.id) {
    throw new Error(
      'Jogo inválido para giro offline',
    )
  }

  const db =
    await getOfflineDatabase()

  return new Promise(
    (resolve, reject) => {
      const transaction =
        db.transaction(
          [
            STORES.PRIZES,
            STORES.SPINS,
          ],
          'readwrite',
        )

      const prizesStore =
        transaction.objectStore(
          STORES.PRIZES,
        )

      const spinsStore =
        transaction.objectStore(
          STORES.SPINS,
        )

      const getPrizesRequest =
        prizesStore.get(
          game.id,
        )

      let createdSpin = null

      getPrizesRequest.onerror =
        () => {
          reject(
            getPrizesRequest.error ||
              new Error(
                'Erro ao carregar prêmios offline',
              ),
          )
        }

      getPrizesRequest.onsuccess =
        () => {
          try {
            const prizeRecord =
              getPrizesRequest.result

            const prizes =
              Array.isArray(
                prizeRecord?.prizes,
              )
                ? [
                    ...prizeRecord.prizes,
                  ]
                : []

            if (
              !prizes.length
            ) {
              transaction.abort()

              reject(
                new Error(
                  'Nenhum prêmio disponível no armazenamento offline',
                ),
              )

              return
            }

            const selectedPrize =
              selectPrizeByWeight(
                prizes,
              )

            if (
              !selectedPrize
            ) {
              transaction.abort()

              reject(
                new Error(
                  'Nenhum prêmio disponível para sorteio offline',
                ),
              )

              return
            }

            const prizeIndex =
              prizes.findIndex(
                (prize) =>
                  prize.id ===
                  selectedPrize.id,
              )

            if (
              prizeIndex < 0
            ) {
              transaction.abort()

              reject(
                new Error(
                  'Prêmio sorteado não encontrado no estoque local',
                ),
              )

              return
            }

            const currentStock =
              normalizeStock(
                selectedPrize
                  .stockAvailable,
              )

            let stockAfter =
              currentStock

            /*
             * Só desconta quando existe
             * controle numérico de estoque.
             */
            if (
              currentStock !==
              null
            ) {
              if (
                currentStock <= 0
              ) {
                transaction.abort()

                reject(
                  new Error(
                    'Prêmio sem estoque offline',
                  ),
                )

                return
              }

              stockAfter =
                currentStock - 1

              prizes[prizeIndex] = {
                ...selectedPrize,

                stockAvailable:
                  stockAfter,
              }
            }

            /*
             * Atualiza o estoque local.
             */
            prizesStore.put({
              ...prizeRecord,

              gameId:
                game.id,

              prizes,

              offlineUpdatedAt:
                new Date()
                  .toISOString(),
            })

            const localId =
              createLocalId(
                'spin',
              )

            const clientOperationId =
              createLocalId(
                'operation',
              )

            const now =
              new Date()
                .toISOString()

            createdSpin = {
              localId,

              clientOperationId,

              gameId:
                game.id,

              gameSlug:
                game.slug,

              leadId:
                leadId ||
                null,

              participant:
                participant ||
                null,

              prizeId:
                selectedPrize.id,

              prize: {
                ...selectedPrize,

                stockAvailable:
                  stockAfter,
              },

              stockBefore:
                currentStock,

              stockAfter,

              syncStatus:
                SPIN_STATUS
                  .PENDING_SYNC,

              syncAttempts:
                0,

              serverSpinId:
                null,

              lastSyncError:
                null,

              createdAt:
                now,

              updatedAt:
                now,
            }

            spinsStore.put(
              createdSpin,
            )
          } catch (error) {
            try {
              transaction.abort()
            } catch (
              abortError
            ) {
              // ignora
            }

            reject(error)
          }
        }

      transaction.oncomplete =
        () => {
          if (
            createdSpin
          ) {
            resolve(
              createdSpin,
            )
          }
        }

      transaction.onerror =
        () => {
          reject(
            transaction.error ||
              new Error(
                'Erro ao salvar giro offline',
              ),
          )
        }

      transaction.onabort =
        () => {
          /*
           * O reject específico normalmente
           * já aconteceu antes.
           */
        }
    },
  )
}

const getOfflineSpin = async (
  localId,
) => {
  const db =
    await getOfflineDatabase()

  return new Promise(
    (resolve, reject) => {
      const transaction =
        db.transaction(
          STORES.SPINS,
          'readonly',
        )

      const store =
        transaction.objectStore(
          STORES.SPINS,
        )

      const request =
        store.get(localId)

      request.onsuccess =
        () => {
          resolve(
            request.result ||
              null,
          )
        }

      request.onerror =
        () => {
          reject(
            request.error,
          )
        }
    },
  )
}

const getPendingSpins =
  async () => {
    const db =
      await getOfflineDatabase()

    return new Promise(
      (resolve, reject) => {
        const transaction =
          db.transaction(
            STORES.SPINS,
            'readonly',
          )

        const store =
          transaction.objectStore(
            STORES.SPINS,
          )

        const index =
          store.index(
            'syncStatus',
          )

        const request =
          index.getAll(
            SPIN_STATUS
              .PENDING_SYNC,
          )

        request.onsuccess =
          () => {
            resolve(
              request.result ||
                [],
            )
          }

        request.onerror =
          () => {
            reject(
              request.error,
            )
          }
      },
    )
  }

const markSpinSynced = async (
  spin,
  serverSpinId,
) => {
  if (!spin?.localId) {
    throw new Error(
      'SPIN_LOCAL_ID_REQUIRED',
    )
  }

  const db =
    await getOfflineDatabase()

  const updatedSpin = {
    ...spin,

    serverSpinId:
      serverSpinId || null,

    syncStatus:
      SPIN_STATUS.SYNCED,

    syncAttempts:
      Number(
        spin.syncAttempts || 0,
      ),

    lastSyncError:
      null,

    lastSyncAttemptAt:
      new Date().toISOString(),

    syncedAt:
      new Date().toISOString(),

    updatedAt:
      new Date().toISOString(),
  }

  return new Promise(
    (resolve, reject) => {
      const transaction =
        db.transaction(
          STORES.SPINS,
          'readwrite',
        )

      const store =
        transaction.objectStore(
          STORES.SPINS,
        )

      const request =
        store.put(
          updatedSpin,
        )

      request.onsuccess =
        () => {
          resolve(
            updatedSpin,
          )
        }

      request.onerror =
        () => {
          reject(
            request.error,
          )
        }
    },
  )
}

const markSpinPending = async (
  spin,
  error = null,
) => {
  if (!spin?.localId) {
    throw new Error(
      'SPIN_LOCAL_ID_REQUIRED',
    )
  }

  const db =
    await getOfflineDatabase()

  const updatedSpin = {
    ...spin,

    syncStatus:
      SPIN_STATUS.PENDING_SYNC,

    syncAttempts:
      Number(
        spin.syncAttempts || 0,
      ) + 1,

    lastSyncAttemptAt:
      new Date().toISOString(),

    lastSyncError:
      error
        ? String(
            error?.message ||
              error,
          )
        : null,

    updatedAt:
      new Date().toISOString(),
  }

  return new Promise(
    (resolve, reject) => {
      const transaction =
        db.transaction(
          STORES.SPINS,
          'readwrite',
        )

      const store =
        transaction.objectStore(
          STORES.SPINS,
        )

      const request =
        store.put(
          updatedSpin,
        )

      request.onsuccess =
        () => {
          resolve(
            updatedSpin,
          )
        }

      request.onerror =
        () => {
          reject(
            request.error,
          )
        }
    },
  )
}

const markSpinError = async (
  spin,
  error,
) => {
  if (!spin?.localId) {
    throw new Error(
      'SPIN_LOCAL_ID_REQUIRED',
    )
  }

  const db =
    await getOfflineDatabase()

  const updatedSpin = {
    ...spin,

    syncStatus:
      SPIN_STATUS.ERROR,

    syncAttempts:
      Number(
        spin.syncAttempts || 0,
      ) + 1,

    lastSyncAttemptAt:
      new Date().toISOString(),

    lastSyncError:
      String(
        error?.message ||
          error ||
          'Erro desconhecido',
      ),

    updatedAt:
      new Date().toISOString(),
  }

  return new Promise(
    (resolve, reject) => {
      const transaction =
        db.transaction(
          STORES.SPINS,
          'readwrite',
        )

      const store =
        transaction.objectStore(
          STORES.SPINS,
        )

      const request =
        store.put(
          updatedSpin,
        )

      request.onsuccess =
        () => {
          resolve(
            updatedSpin,
          )
        }

      request.onerror =
        () => {
          reject(
            request.error,
          )
        }
    },
  )
}

const OfflineSpinService = {
  createOfflineSpin,

  getOfflineSpin,
  getPendingSpins,

  markSpinSynced,
  markSpinPending,
  markSpinError,

  selectPrizeByWeight,
}

export {
  SPIN_STATUS,
}

export default OfflineSpinService